import {
  AppMcpStatus,
  ConversationCreateRequest,
  ConversationRenameRequest,
  ConversationMessageResponse,
  ConversationResponse,
  ScreenContext,
  YbotAvailabilityResponse,
  YdistriDevsMcpStatus,
} from '@ydistri/api-sdk';
import { apiSlice, ErrorLike, errorTransform } from '../../../apis/api';
import { constructRequestParams, getTags } from '../../../apis/apiLib';
import { YbotCollection } from '../../../swagger/collections';

const { TAGS, TAGS_ARRAY } = getTags('ybot');

/** HTTP status of a failed call; a network-level failure carries no `response` at all. */
const statusOf = (error: unknown): number | undefined => {
  if (typeof error === 'object' && error !== null && 'response' in error) {
    // eslint-disable-next-line @typescript-eslint/consistent-type-assertions -- narrowed by the `in` check above
    const { response } = error as { response?: { status?: number } };
    return response?.status;
  }

  return undefined;
};

export interface YbotMessageCreatePayload {
  conversationId: string;
  /** Sent as the project header, which every POST needs: the audit filter resolves the project. */
  projectShortName: string;
  prompt: string;
  /** Minted client-side: a stream event for this turn can beat this call's own response. */
  turnId: string;
  /**
   * Where the prompt was sent from. The server resolves the project and its database itself; this
   * is the part only the browser knows. Absent when nothing matched the route table.
   */
  screen?: ScreenContext;
}

export interface YbotConversationPayload {
  conversationId: string;
  projectShortName: string;
}

export type YbotResetPayload = YbotConversationPayload;

export interface YbotRenamePayload extends YbotConversationPayload {
  title: ConversationRenameRequest['title'];
}

export interface YbotResetResult {
  /** False on a 409 - the turn had already ended, which is not an error to show. */
  reset: boolean;
}

export const apiYbot = apiSlice.enhanceEndpoints({ addTagTypes: TAGS_ARRAY }).injectEndpoints({
  endpoints: builder => ({
    // The gate for everything else; callers treat "no data" as unavailable.
    getYbotAvailability: builder.query<YbotAvailabilityResponse, undefined>({
      queryFn: async () => {
        try {
          const response = await YbotCollection.availabilityList();
          return { data: response.data.data };
        } catch (error) {
          // eslint-disable-next-line @typescript-eslint/consistent-type-assertions -- the shape is guaranteed by the generated SDK
          return { error: errorTransform(error as ErrorLike) };
        }
      },
      // Fixed key: the root serializer keys by selected project, but availability is not
      // project-scoped - per-project keys would refetch and hide the trigger on every switch.
      serializeQueryArgs: () => 'getYbotAvailability',
      providesTags: [TAGS.availability],
    }),

    getYbotConversations: builder.query<ConversationResponse[], string>({
      queryFn: async projectShortName => {
        try {
          const response = await YbotCollection.conversationsList({ projectShortName });
          return { data: response.data.data };
        } catch (error) {
          // eslint-disable-next-line @typescript-eslint/consistent-type-assertions -- the shape is guaranteed by the generated SDK
          return { error: errorTransform(error as ErrorLike) };
        }
      },
      providesTags: result => [
        TAGS.conversations,
        ...(result ?? []).map(conversation => ({
          type: TAGS.conversation,
          id: conversation.id,
        })),
      ],
    }),

    // 201 with a Location header and no body: the id was minted by the caller, so there is
    // nothing to read back. The dock already knows what it created.
    createYbotConversation: builder.mutation<void, ConversationCreateRequest>({
      queryFn: async request => {
        try {
          await YbotCollection.conversationsCreate(
            request,
            constructRequestParams({ projectShortName: request.projectShortName }),
          );
          return { data: undefined };
        } catch (error) {
          // eslint-disable-next-line @typescript-eslint/consistent-type-assertions -- the shape is guaranteed by the generated SDK
          return { error: errorTransform(error as ErrorLike) };
        }
      },
      invalidatesTags: [TAGS.conversations],
    }),

    getYbotMessages: builder.query<ConversationMessageResponse[], string>({
      queryFn: async conversationId => {
        try {
          const response = await YbotCollection.conversationsMessagesList(conversationId);
          return { data: response.data.data };
        } catch (error) {
          // eslint-disable-next-line @typescript-eslint/consistent-type-assertions -- the shape is guaranteed by the generated SDK
          return { error: errorTransform(error as ErrorLike) };
        }
      },
      providesTags: (result, error, conversationId) => [
        { type: TAGS.messages, id: conversationId },
      ],
    }),

    // Answers as soon as the turn is accepted; the reply itself arrives over the hub.
    createYbotMessage: builder.mutation<undefined, YbotMessageCreatePayload>({
      queryFn: async ({ conversationId, projectShortName, prompt, turnId, screen }) => {
        try {
          await YbotCollection.conversationsMessagesCreate(
            conversationId,
            { prompt, turnId, screen },
            constructRequestParams({ projectShortName }),
          );
          return { data: undefined };
        } catch (error) {
          // eslint-disable-next-line @typescript-eslint/consistent-type-assertions -- the shape is guaranteed by the generated SDK
          return { error: errorTransform(error as ErrorLike) };
        }
      },
      invalidatesTags: (result, error, { conversationId }) => [
        TAGS.conversations,
        { type: TAGS.conversation, id: conversationId },
        { type: TAGS.messages, id: conversationId },
      ],
    }),

    renameYbotConversation: builder.mutation<ConversationResponse, YbotRenamePayload>({
      queryFn: async ({ conversationId, projectShortName, title }) => {
        try {
          const response = await YbotCollection.conversationsPartialUpdate(
            conversationId,
            { title },
            constructRequestParams({ projectShortName }),
          );
          return { data: response.data.data };
        } catch (error) {
          // eslint-disable-next-line @typescript-eslint/consistent-type-assertions -- the shape is guaranteed by the generated SDK
          return { error: errorTransform(error as ErrorLike) };
        }
      },
      invalidatesTags: (result, error, { conversationId }) => [
        TAGS.conversations,
        { type: TAGS.conversation, id: conversationId },
      ],
    }),

    deleteYbotConversation: builder.mutation<undefined, YbotConversationPayload>({
      queryFn: async ({ conversationId, projectShortName }) => {
        try {
          await YbotCollection.conversationsDelete(
            conversationId,
            constructRequestParams({ projectShortName }),
          );
          return { data: undefined };
        } catch (error) {
          // eslint-disable-next-line @typescript-eslint/consistent-type-assertions -- the shape is guaranteed by the generated SDK
          return { error: errorTransform(error as ErrorLike) };
        }
      },
      // Deliberately not the messages tag: the transcript went with the conversation, and
      // refetching it is a guaranteed 404.
      invalidatesTags: (result, error, { conversationId }) => [
        TAGS.conversations,
        { type: TAGS.conversation, id: conversationId },
      ],
    }),

    // Not project-scoped, same fixed key as availability: the login state lives on this
    // developer's machine, not per project.
    getYdistriDevsMcpStatus: builder.query<YdistriDevsMcpStatus, undefined>({
      queryFn: async () => {
        try {
          const response = await YbotCollection.mcpYdistriDevsStatusList();
          return { data: response.data.data.status };
        } catch (error) {
          // eslint-disable-next-line @typescript-eslint/consistent-type-assertions -- the shape is guaranteed by the generated SDK
          return { error: errorTransform(error as ErrorLike) };
        }
      },
      serializeQueryArgs: () => 'getYdistriDevsMcpStatus',
      providesTags: [TAGS.ydistriDevsMcpStatus],
    }),

    // The API's self-check of its own MCP endpoint - the way a turn reaches it. Not project-scoped
    // and no login: being logged into the application is being logged into the app MCP.
    getAppMcpStatus: builder.query<AppMcpStatus, undefined>({
      queryFn: async () => {
        try {
          const response = await YbotCollection.mcpAppStatusList();
          return { data: response.data.data.status };
        } catch (error) {
          // eslint-disable-next-line @typescript-eslint/consistent-type-assertions -- the shape is guaranteed by the generated SDK
          return { error: errorTransform(error as ErrorLike) };
        }
      },
      serializeQueryArgs: () => 'getAppMcpStatus',
      providesTags: [TAGS.appMcpStatus],
    }),

    // Answers as soon as `claude mcp login` has started; it opens a browser and the developer
    // finishes the flow there. The caller re-polls the status query for the outcome.
    startYdistriDevsMcpLogin: builder.mutation<undefined, undefined>({
      queryFn: async () => {
        try {
          await YbotCollection.mcpYdistriDevsLoginCreate();
          return { data: undefined };
        } catch (error) {
          // eslint-disable-next-line @typescript-eslint/consistent-type-assertions -- the shape is guaranteed by the generated SDK
          return { error: errorTransform(error as ErrorLike) };
        }
      },
      invalidatesTags: [TAGS.ydistriDevsMcpStatus],
    }),

    resetYbotTurn: builder.mutation<YbotResetResult, YbotResetPayload>({
      queryFn: async ({ conversationId, projectShortName }) => {
        try {
          await YbotCollection.conversationsResetCreate(
            conversationId,
            constructRequestParams({ projectShortName }),
          );
          return { data: { reset: true } };
        } catch (error) {
          if (statusOf(error) === 409) {
            return { data: { reset: false } };
          }

          // eslint-disable-next-line @typescript-eslint/consistent-type-assertions -- the shape is guaranteed by the generated SDK
          return { error: errorTransform(error as ErrorLike) };
        }
      },
      invalidatesTags: (result, error, { conversationId }) => [
        TAGS.conversations,
        { type: TAGS.conversation, id: conversationId },
        { type: TAGS.messages, id: conversationId },
      ],
    }),
  }),
});

/** Everything a finished turn changes; used by the hub handlers, which have no mutation of their own. */
export const ybotConversationTags = (conversationId: string | undefined) =>
  conversationId === undefined
    ? [TAGS.conversations]
    : [
        TAGS.conversations,
        { type: TAGS.conversation, id: conversationId },
        { type: TAGS.messages, id: conversationId },
      ];

export const {
  useGetYbotAvailabilityQuery,
  useGetYbotConversationsQuery,
  useCreateYbotConversationMutation,
  useGetYbotMessagesQuery,
  useCreateYbotMessageMutation,
  useRenameYbotConversationMutation,
  useDeleteYbotConversationMutation,
  useResetYbotTurnMutation,
  useGetYdistriDevsMcpStatusQuery,
  useGetAppMcpStatusQuery,
  useStartYdistriDevsMcpLoginMutation,
} = apiYbot;
